import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ChevronRight } from 'lucide-react';

const Hero = () => {
  const { t } = useTranslation();
  const [logoState, setLogoState] = useState('drop'); // 'drop', 'visible', 'fly-up', 'hidden'
  const [hasScrolled, setHasScrolled] = useState(false);
  
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Ha az oldal tetején vagyunk (0-100px)
      if (currentScrollY < 100) {
        if (hasScrolled) {
          // Visszascrolloztunk a tetejére - újra leesik a logo
          setLogoState('drop');
          setHasScrolled(false);
        }
      } else if (currentScrollY > 100 && !hasScrolled) {
        // Elkezd scrollozni lefele - logo felszáll
        setLogoState('fly-up');
        setHasScrolled(true);
        
        // Után hidden state
        setTimeout(() => {
          setLogoState('hidden');
        }, 600);
      }
      
      lastScrollY = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);
  
  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Logo animáció osztály
  const getLogoClass = () => {
    switch(logoState) {
      case 'drop':
        return 'logo-drop-down';
      case 'fly-up':
        return 'logo-fly-up';
      case 'hidden':
        return 'logo-hidden';
      default:
        return '';
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-visible bg-black w-full max-w-full pb-16 sm:pb-24">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_groom-station-1/artifacts/bsvy4anb_IMG_3015.jpeg"
          alt="Velvet Cuts szalon"
          className="w-full h-full object-cover grayscale opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-20 text-center">
        {/* Logo - ZÁSZLÓ ANIMÁCIÓVAL */}
        <div className={`mb-8 sm:mb-12 flex justify-center ${getLogoClass()}`}>
          <img
            src="https://customer-assets.emergentagent.com/job_groom-station-1/artifacts/qi23svjr_IMG_0898.jpeg"
            alt="Velvet Cuts Logo"
            className="w-64 sm:w-72 md:w-80 lg:w-96 h-auto"
            loading="eager"
          />
        </div>

        <p className="text-xs md:text-sm text-gray-400 mb-3 sm:mb-4 font-montserrat font-light tracking-[0.3em] uppercase">
          {t('hero.subtitle')}
        </p>
        
        <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-oswald font-bold text-white mb-4 sm:mb-6 tracking-tighter uppercase leading-tight">
          {t('hero.title')}
        </h1>
        
        <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-3 sm:mb-4 max-w-3xl mx-auto font-montserrat font-light leading-relaxed px-4">
          {t('hero.description1')}
        </p>

        <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto font-montserrat px-4">
          {t('hero.description2')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-20 px-4">
          <Button
            onClick={scrollToBooking}
            className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 transition-all duration-300 uppercase tracking-wider text-xs sm:text-sm font-bold py-5 sm:py-6 px-8 sm:px-10 rounded-sm group"
          >
            {t('hero.cta1')}
            <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <Button
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            variant="outline"
            className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-black transition-all duration-300 uppercase tracking-wider text-xs sm:text-sm font-bold py-5 sm:py-6 px-8 sm:px-10 rounded-sm"
          >
            {t('hero.cta2')}
          </Button>
        </div>

        {/* Scroll Indicator - GOMBOK ALATT KÖZÉPEN */}
        <div className="mb-12 sm:mb-20 flex justify-center">
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center animate-bounce">
            <div className="w-1 h-2 sm:h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>

        {/* Key Points - EXTRA SPACING FELÜL */}
        <div className="grid grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto border-t border-neutral-800 pt-8 sm:pt-12 px-4 mt-8 sm:mt-12">
          <div className="text-center px-2">
            <p className="text-white font-oswald font-medium uppercase text-xs sm:text-sm tracking-wider mb-1 leading-tight">{t('hero.feature1')}</p>
            <p className="text-gray-500 text-[10px] sm:text-xs font-montserrat leading-tight">{t('hero.feature1Sub')}</p>
          </div>
          <div className="text-center px-2">
            <p className="text-white font-oswald font-medium uppercase text-xs sm:text-sm tracking-wider mb-1 leading-tight">{t('hero.feature2')}</p>
            <p className="text-gray-500 text-[10px] sm:text-xs font-montserrat leading-tight">{t('hero.feature2Sub')}</p>
          </div>
          <div className="text-center px-2">
            <p className="text-white font-oswald font-medium uppercase text-xs sm:text-sm tracking-wider mb-1 leading-tight">{t('hero.feature3')}</p>
            <p className="text-gray-500 text-[10px] sm:text-xs font-montserrat leading-tight">{t('hero.feature3Sub')}</p>
          </div>
          <div className="text-center px-2">
            <p className="text-white font-oswald font-medium uppercase text-xs sm:text-sm tracking-wider mb-1 leading-tight">{t('hero.feature4')}</p>
            <p className="text-gray-500 text-[10px] sm:text-xs font-montserrat leading-tight">{t('hero.feature4Sub')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
